import { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { useAuth0 } from '@auth0/auth0-react'
import { CopilotKitProvider, CopilotKitProviderProps } from '@copilotkit/react-core/v2'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useConfigStore } from '@stores'
import { AI_CHAT_AGENT_ID } from 'src/constants/aiChat'
import { chatErrorMessage, tokenRefreshDelay } from './utils'

// Reported per run by the chat itself, which can tell a live error from one
// replayed with the thread's history (`useRunErrorNotifications`).
const RUN_ERROR_EVENT_CODE = 'agent_run_error_event'

const RENDER_THROTTLE_MS = 50

interface Props {
  /** The AG-UI `forwardedProps` of every run (`AIChatRunProperties`). */
  properties: Record<string, unknown>
  /** Shown instead of the chat until the first token is there. */
  fallback: ReactNode
  children: ReactNode
}

/**
 * The CopilotKit provider of the in-app AI chat, mounted per project by the
 * navbar chat (`AIChatSession`), which stays mounted across editor pages so
 * the chat thread survives navigation. The run properties come in as a prop:
 * the provider re-applies `properties` whenever it reconfigures the core
 * (e.g. on every token refresh), so values set on the core directly would
 * be reset. The browser token travels as the Authorization header to the
 * Copilot runtime, which exchanges it for a per-turn run token at the API
 * (plan/ai-chat-architecture §2). The header is read once per render, not
 * per request, so the token is renewed before Auth0 would hand out a new
 * one (`tokenRefreshDelay`), also in a background tab, and the chat waits
 * for the first one. Without a configured runtime URL the provider is
 * skipped (the navbar button renders disabled before it).
 */
const CopilotProvider = ({ properties, fallback, children }: Props): ReactElement => {
  const copilotRuntimeUrl = useConfigStore(state => state.copilotRuntimeUrl)
  const auth0Audience = useConfigStore(state => state.auth0Audience)
  const auth0Scopes = useConfigStore(state => state.auth0Scopes)
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const { enqueueSnackbar } = useSnackbar()

  const { data: accessToken } = useQuery({
    queryKey: ['copilotAccessToken'],
    queryFn: () =>
      getAccessTokenSilently({
        authorizationParams: { audience: auth0Audience, scope: auth0Scopes },
      }),
    enabled: isAuthenticated && !!copilotRuntimeUrl,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: token => tokenRefreshDelay(token, Date.now()),
    refetchIntervalInBackground: true,
    onError: (error: Error) => {
      enqueueSnackbar(buildErrorMessage(error, 'KI-Chat: Anmeldung fehlgeschlagen'), {
        variant: 'error',
      })
    },
  })

  // Model failures during a turn come back from the agent service as a chat
  // message. What still arrives here (the runtime or the authorize step
  // refusing a turn, a broken connection) is surfaced here, once, for every
  // chat surface.
  const handleError = useCallback<NonNullable<CopilotKitProviderProps['onError']>>(
    ({ error, code }) => {
      if (code === RUN_ERROR_EVENT_CODE) return
      enqueueSnackbar(buildErrorMessage(error, chatErrorMessage(error, code)), {
        variant: 'error',
      })
    },
    [enqueueSnackbar],
  )

  const headers = useMemo(
    (): Record<string, string> => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    [accessToken],
  )

  if (!copilotRuntimeUrl) return <>{children}</>
  if (!accessToken) return <>{fallback}</>

  return (
    <CopilotKitProvider
      runtimeUrl={copilotRuntimeUrl}
      headers={headers}
      properties={properties}
      agentId={AI_CHAT_AGENT_ID}
      // CopilotKit's floating dev inspector has no placement option and would
      // sit over the navbar; the chat drawer is our only surface.
      showDevConsole={false}
      enableInspector={false}
      // Re-render the chat at most every 50 ms while it streams (CopilotKit's
      // own throttle, leading + trailing). Unthrottled, every token renders:
      // a long turn of a reasoning model streams tens of thousands of single
      // tokens, and replaying such a thread on reconnect ran the tab out of
      // memory. Run lifecycle callbacks are not throttled.
      defaultThrottleMs={RENDER_THROTTLE_MS}
      onError={handleError}
    >
      {children}
    </CopilotKitProvider>
  )
}

export default CopilotProvider
